import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PrintQueue, GroupingStrategy, PrintQueueEventPayload } from '../types';
import { sanitizeQueueForStore } from './serializers';
import { QueueState } from '../QueueManager';

export interface PrintingState extends QueueState {
  error: string | null;
  warnings: string[];
  activeJobId: string | null;
  retryCounts: Record<string, number>;
  lastEvent: PrintQueueEventPayload | null;
}

const initialState: PrintingState = {
  currentQueue: null,
  isProcessing: false,
  completedJobs: [],
  failedJobs: [],
  progress: {
    completed: 0,
    total: 0,
    percentage: 0
  },
  error: null,
  warnings: [],
  activeJobId: null,
  retryCounts: {},
  lastEvent: null,
};

const printingSlice = createSlice({
  name: 'printing',
  initialState,
  reducers: {
    setQueue: {
      reducer: (state, action: PayloadAction<PrintQueue>) => {
        state.currentQueue = action.payload;
        state.progress.total = action.payload.jobs.length;
        state.progress.completed = 0;
        state.progress.percentage = 0;
        state.completedJobs = [];
        state.failedJobs = [];
        state.error = null;
        state.warnings = [];
        state.retryCounts = {};
        state.activeJobId = null;
        state.lastEvent = null;
      },
      prepare: (queue: PrintQueue) => ({
        payload: sanitizeQueueForStore(queue),
      }),
    },
    
    addJobsToQueue: {
      reducer: (state, action: PayloadAction<PrintQueue>) => {
        if (!state.currentQueue) {
          state.currentQueue = action.payload;
        } else {
          state.currentQueue.jobs.push(...action.payload.jobs);
          state.currentQueue.totalPages += action.payload.totalPages;
        }
        state.progress.total = state.currentQueue.jobs.length;
      },
      prepare: (queue: PrintQueue) => ({
        payload: sanitizeQueueForStore(queue),
      }),
    },
    
    setGroupingStrategy: (state, action: PayloadAction<GroupingStrategy>) => {
      if (state.currentQueue) {
        state.currentQueue.groupingStrategy = action.payload;
      }
    },
    
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setWarnings: (state, action: PayloadAction<string[]>) => {
      state.warnings = action.payload;
    },

    recordJobStarted: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      state.activeJobId = action.payload.jobId;
      state.isProcessing = true;
      state.lastEvent = action.payload;
    },

    recordJobRetrying: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      const { jobId, attempt, message, reason } = action.payload;
      const current = state.retryCounts[jobId] ?? 0;
      state.retryCounts[jobId] = current + 1;
      const parts = [] as string[];
      if (attempt) parts.push(`Tentative ${attempt}`);
      if (reason) parts.push(reason);
      if (message) parts.push(message);
      const composed = parts.length > 0 ? parts.join(' - ') : `Nouvelle tentative (${state.retryCounts[jobId]})`;
      const entry = `Job ${jobId}: ${composed}`;
      if (!state.warnings.includes(entry)) {
        state.warnings.push(entry);
      }
      state.lastEvent = action.payload;
    },

    recordJobWaitingOnPrinter: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      const { jobId, message, reason } = action.payload;
      state.activeJobId = jobId;
      state.isProcessing = true;
      const parts = [reason, message].filter(Boolean) as string[];
      if (parts.length > 0) {
        const entry = `Job ${jobId}: ${parts.join(' - ')}`;
        if (!state.warnings.includes(entry)) {
          state.warnings.push(entry);
        }
      }
      state.lastEvent = action.payload;
    },

    recordJobWarning: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      const { jobId, warnings: warningList, message } = action.payload;
      const entries = warningList && warningList.length > 0 ? warningList : message ? [message] : [];
      for (const warning of entries) {
        const entry = `Job ${jobId}: ${warning}`;
        if (!state.warnings.includes(entry)) {
          state.warnings.push(entry);
        }
      }
      state.lastEvent = action.payload;
    },
    
    markJobCompleted: (state, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (state.currentQueue) {
        const nextJobs = state.currentQueue.jobs.filter(job => job.id !== jobId);
        if (nextJobs.length !== state.currentQueue.jobs.length) {
          state.currentQueue.jobs = nextJobs;
          state.currentQueue.totalPages = nextJobs.reduce((total, job) => total + job.metadata.pageCount, 0);
        }
      }
      if (!state.completedJobs.includes(jobId)) {
        state.completedJobs.push(jobId);
        state.progress.completed = state.completedJobs.length;
        state.progress.percentage = state.progress.total > 0 
          ? Math.round((state.progress.completed / state.progress.total) * 100)
          : 0;
      }
      if (state.currentQueue && state.currentQueue.jobs.length === 0) {
        state.currentQueue = null;
      }
    },

    markJobFailed: (state, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (state.currentQueue) {
        const nextJobs = state.currentQueue.jobs.filter(job => job.id !== jobId);
        if (nextJobs.length !== state.currentQueue.jobs.length) {
          state.currentQueue.jobs = nextJobs;
          state.currentQueue.totalPages = nextJobs.reduce((total, job) => total + job.metadata.pageCount, 0);
        }
      }
      if (!state.failedJobs.includes(jobId)) {
        state.failedJobs.push(jobId);
      }
      if (state.currentQueue && state.currentQueue.jobs.length === 0) {
        state.currentQueue = null;
      }
    },

    recordJobCompleted: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      const { jobId } = action.payload;
      if (state.activeJobId === jobId) {
        state.activeJobId = null;
      }
      delete state.retryCounts[jobId];
      state.isProcessing = state.progress.completed < state.progress.total;
      state.lastEvent = action.payload;
    },

    recordJobFailed: (state, action: PayloadAction<PrintQueueEventPayload>) => {
      const { jobId, reason } = action.payload;
      if (state.activeJobId === jobId) {
        state.activeJobId = null;
      }
      delete state.retryCounts[jobId];
      const entry = reason ? `Job ${jobId}: ${reason}` : `Job ${jobId}: échec`;
      if (!state.warnings.includes(entry)) {
        state.warnings.push(entry);
      }
      state.isProcessing = false;
      state.lastEvent = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isProcessing = false;
    },
    
    clearQueue: (state) => {
      state.currentQueue = null;
      state.isProcessing = false;
      state.completedJobs = [];
      state.failedJobs = [];
      state.progress = {
        completed: 0,
        total: 0,
        percentage: 0
      };
      state.error = null;
      state.warnings = [];
      state.activeJobId = null;
      state.retryCounts = {};
      state.lastEvent = null;
    },

    removeJobFromQueue: (state, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (!state.currentQueue) {
        return;
      }

      const nextJobs = state.currentQueue.jobs.filter(job => job.id !== jobId);
      if (nextJobs.length === state.currentQueue.jobs.length) {
        return;
      }

      state.currentQueue.jobs = nextJobs;
      state.currentQueue.totalPages = nextJobs.reduce(
        (total, job) => total + job.metadata.pageCount,
        0
      );

      state.completedJobs = state.completedJobs.filter(id => id !== jobId);
      state.failedJobs = state.failedJobs.filter(id => id !== jobId);
      delete state.retryCounts[jobId];

      state.progress.total = Math.max(state.progress.total - 1, state.completedJobs.length);
      state.progress.completed = state.completedJobs.length;
      state.progress.percentage =
        state.progress.total > 0
          ? Math.round((state.progress.completed / state.progress.total) * 100)
          : 0;

      if (state.activeJobId === jobId) {
        state.activeJobId = null;
        state.isProcessing = false;
      }

      if (state.currentQueue.jobs.length === 0) {
        state.currentQueue = null;
        state.isProcessing = false;
      }
    },
  }
});

export const {
  setQueue,
  addJobsToQueue,
  setGroupingStrategy,
  setProcessing,
  markJobCompleted,
  markJobFailed,
  recordJobStarted,
  recordJobRetrying,
  recordJobWaitingOnPrinter,
  recordJobWarning,
  recordJobCompleted,
  recordJobFailed,
  setError,
  setWarnings,
  clearQueue,
  removeJobFromQueue
} = printingSlice.actions;

export default printingSlice.reducer;
